import { Link } from "@tanstack/react-router";
import { BRAND } from "@omina/core";

/**
 * The vesica mark: the brand book's pointed oval — a seed, a gem — drawn
 * as two arcs plus an inner facet echoing the outer edge.
 *
 * Two paths and nothing else — no gradient, no mask, no id, no background
 * dependency. It is `currentColor` throughout, so it works on any surface, in
 * either theme, at any colour, and shrinks to a favicon without changing.
 *
 * Two constraints carried over from the eclipse mark this replaces, both
 * learned the hard way:
 *
 * The axis is VERTICAL — points up and down. Laid on its side the same shape
 * is an eye, and on a product that reads your calendar and your mail an eye
 * is the one connotation worth engineering away from.
 *
 * And it must not read as a letter beside the wordmark: a crescent once
 * turned the lockup into **COMINA**. The vesica is closed and pointed, so it
 * cannot become a C; it is kept slender (width ≈ 0.55 of height, the brand
 * book's proportion) so it does not read as an O either.
 */
function VesicaMark({ size = 22 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className="vesica"
      aria-hidden
      focusable="false"
    >
      {/* The stone: two arcs of the same circle, mirrored. */}
      <path
        d="M12 2.5 A11 11 0 0 1 12 21.5 A11 11 0 0 1 12 2.5 Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      {/* The facet: the same shape smaller, a catch of light inside. */}
      <path
        d="M12 6.5 A6.4 6.4 0 0 1 12 17.5 A6.4 6.4 0 0 1 12 6.5 Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinejoin="round"
        opacity="0.55"
      />
    </svg>
  );
}

/** The vesica mark + wordmark.
 *
 *  `markOnly` drops the wordmark and keeps the mark. Sign-in uses it
 *  because the name moved to the foot of that page: the mark opens the screen
 *  and the wordmark closes it, and printing OMINA at both ends would read as a
 *  mistake rather than as a frame. The `aria-label` stays either way, so the
 *  link is still "Omina" to a screen reader with nothing to read inside it. */
export default function Logo({ markOnly = false }: { markOnly?: boolean }) {
  return (
    <Link to="/home" className={markOnly ? "logo markonly" : "logo"} aria-label={BRAND.name}>
      <VesicaMark />
      {!markOnly && <span className="wordmark">{BRAND.wordmark}</span>}
    </Link>
  );
}
